import { useState, useEffect, useMemo } from 'react';
import { 
  getCurrentSemester, 
  getAllSemestersForClass,
  getSemesterBoundaries,
  calculateSemesterProgress 
} from '../utils/dateUtils';

/**
 * Custom hook to manage semester information for a class
 * @param {string|Date} classStartDate - The start date of the class
 * @param {number} totalSemesters - Total number of semesters (default: 6)
 * @returns {Object} Semester information and utilities
 */
export const useSemester = (classStartDate, totalSemesters = 6) => {
  const [currentDate, setCurrentDate] = useState(new Date());

  // Update current date every minute to keep semester info fresh
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentDate(new Date());
    }, 60000); // Update every minute

    return () => clearInterval(interval);
  }, []);

  // Calculate current semester
  const currentSemester = useMemo(() => {
    if (!classStartDate) return null;
    return getCurrentSemester(classStartDate, totalSemesters, currentDate);
  }, [classStartDate, currentDate, totalSemesters]);

  // Get all semesters for the class
  const allSemesters = useMemo(() => {
    if (!classStartDate) return [];
    return getAllSemestersForClass(classStartDate, totalSemesters);
  }, [classStartDate, totalSemesters]);

  // Get a specific semester's boundaries
  const getSemesterInfo = (semesterNumber) => {
    if (!classStartDate || !semesterNumber) return null;
    
    const startDate = new Date(classStartDate);
    const startYear = startDate.getFullYear();
    const startMonth = startDate.getMonth();
    const baseAcademicYear = startMonth >= 7 ? startYear : startYear - 1;
    
    return getSemesterBoundaries(baseAcademicYear, semesterNumber);
  };

  // Check if a semester is the current active semester
  const isCurrentSemester = (semesterNumber) => {
    return currentSemester?.semesterNumber === semesterNumber && currentSemester?.isActive;
  };

  // Check if a semester has ended
  const hasSemesterEnded = (semesterNumber) => {
    if (!currentSemester) return false;
    return semesterNumber < currentSemester.semesterNumber || 
           (semesterNumber === currentSemester.semesterNumber && !currentSemester.isActive);
  };

  // Check if a semester is in the future
  const isFutureSemester = (semesterNumber) => {
    if (!currentSemester) return true;
    return semesterNumber > currentSemester.semesterNumber;
  };

  // Get semester status
  const getSemesterStatus = (semesterNumber) => {
    if (isCurrentSemester(semesterNumber)) return 'active';
    if (hasSemesterEnded(semesterNumber)) return 'completed';
    if (isFutureSemester(semesterNumber)) return 'upcoming';
    return 'unknown';
  };

  return {
    // Current semester info
    currentSemester,
    
    // All semesters
    allSemesters,
    
    // Utility functions
    getSemesterInfo,
    isCurrentSemester,
    hasSemesterEnded,
    isFutureSemester,
    getSemesterStatus,
    
    // Helper values
    isLoading: !classStartDate,
    hasActiveSemester: currentSemester?.isActive || false,
  };
};

/**
 * Custom hook for displaying semester progress
 * @param {Date} startDate - Semester start date
 * @param {Date} endDate - Semester end date
 * @returns {Object} Progress information
 */
export const useSemesterProgress = (startDate, endDate) => {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const updateProgress = () => {
      if (!startDate || !endDate) {
        setProgress(0);
        return;
      }
      
      const newProgress = calculateSemesterProgress(startDate, endDate);
      setProgress(newProgress);
    };

    // Initial calculation
    updateProgress();

    // Update every hour
    const interval = setInterval(updateProgress, 3600000);

    return () => clearInterval(interval);
  }, [startDate, endDate]);

  return {
    progress,
    isComplete: progress >= 100,
    isStarted: progress > 0,
  };
};

export default useSemester;
